import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Modal,
  Spin,
  message,
  Select,
  Switch,
  Upload,
  Button,
  Row,
  Col,
  InputNumber,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useUserContext } from "../context/dashboard-context";
import ProductCard from "../components/product-card/product-card";
import CustomPagination from "../components/pagination";
import VariantForm from "../components/product-card/VariantForm";
import api from "../context/api";
import "./products.scss";

const { TextArea } = Input;
const { Option } = Select;

const ProductsPage = () => {
  const {
    token,
    products = [],
    categorys = [],
    GetProducts,
    totalProducts,
    GetCategory,
    loading: contextLoading,
  } = useUserContext();

  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [isVariantModalOpen, setIsVariantModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [hasVariants, setHasVariants] = useState(false);
  const [images, setImages] = useState([]);
  const [variants, setVariants] = useState([]);
  const [imagesToDelete, setImagesToDelete] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        await Promise.all([
          GetProducts(currentPage),
          categorys.length ? Promise.resolve() : GetCategory(),
        ]);
      } catch (error) {
        console.error("Initial data loading error:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [currentPage]);

  const normFile = (e) => (Array.isArray(e) ? e : e?.fileList || []);

  const handleImageChange = ({ fileList }) => setImages(fileList);

  const handleImageRemove = (file) => {
    if (file.id) {
      setImagesToDelete((prev) => [...prev, file.id]);
    }
    setImages((prev) => prev.filter((img) => img.uid !== file.uid));
    return true;
  };

  const resetForm = () => {
    form.resetFields();
    setImages([]);
    setVariants([]);
    setImagesToDelete([]);
    setHasVariants(false);
    setSelectedProduct(null);
    setIsModalOpen(false);
    setIsVariantModalOpen(false);
    setEditingIndex(null); // Add this line
  };

  const handleAddProduct = async (values) => {
    setLoading(true);
    const formData = new FormData();
  
    // Basic product fields
    formData.append("title_uz", values.title_uz);
    formData.append("title_ru", values.title_ru);
    formData.append("title_en", values.title_en || "");
    formData.append("description_uz", values.description_uz);
    formData.append("description_ru", values.description_ru);
    formData.append("description_en", values.description_en || "");
    formData.append("category", values.category);
    formData.append("is_popular", values.is_popular || false);
    formData.append("has_variants", hasVariants);
  
    if (!hasVariants) {
      // For products WITHOUT variants
      formData.append("price", values.price);
      
      if (selectedProduct) {
        // PUT request for existing product without variants
        if (images.length > 0) {
          // If new images are provided, replace all existing ones
          images.forEach((image) => {
            if (image.originFileObj) {
              formData.append("product_images", image.originFileObj);
            }
          });
        } else {
          // If no new images, keep existing ones
          formData.append("keep_existing_images", "true");
        }
      } else {
        // POST request for new product without variants
        images.forEach((image) => {
          if (image.originFileObj) {
            formData.append("product_images", image.originFileObj);
          }
        });
      }
    } else {
      // For products WITH variants
      const variantsPayload = variants.map((variant, index) => {
        const variantData = {
          size: String(variant.size).trim(),
          price: Number(variant.price)
        };
        
        // Include variant ID if it exists (for updates)
        if (variant.id) {
          variantData.id = variant.id;
        }
        
        // Include existing image IDs to prevent deletion
        if (variant.images) {
          variantData.existing_images = variant.images
            .filter(img => img.id) // Only keep images with IDs
            .map(img => img.id);
        }
        
        return variantData;
      });
      
      formData.append("variants_data", JSON.stringify(variantsPayload));
  
      // Handle new image uploads for variants
      variants.forEach((variant, variantIndex) => {
        if (variant.images) {
          variant.images.forEach((image) => {
            if (image.originFileObj) {
              // Newly uploaded images
              formData.append(`variant_${variantIndex}_images`, image.originFileObj);
            }
          });
        }
      });
    }
  
    // Debug: Log all form data entries
    console.log("FormData contents:");
    for (let [key, value] of formData.entries()) {
      console.log(key, value instanceof File ? `${value.name} (File)` : value);
    }
  
    try {
      const method = selectedProduct ? "PUT" : "POST";
      const url = selectedProduct ? `/products/${selectedProduct.id}/` : "/products/";
      
      const response = await api({
        method,
        url,
        data: formData,
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });
  
      if ([200, 201].includes(response.status)) {
        message.success(selectedProduct ? "Yangilandi" : "Qo'shildi");
        await GetProducts(currentPage);
        resetForm();
      }
    } catch (error) {
      console.error("API Error:", {
        request: error.config?.data,
        response: error.response?.data
      });
      message.error(error.response?.data?.message || "Xatolik yuz berdi, rasmlarni kichikroq razmerini qo'shing");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (product) => {
    setSelectedProduct(product);
    setHasVariants(product.has_variants);
    setImagesToDelete([]);
  
    // Prepare variants data with proper image references
    const productVariants = product.variants.map((variant) => {
      const variantData = {
        id: variant.id, // Include variant ID
        size: variant.size,
        price: parseFloat(variant.price),
      };
      
      // Map images with proper references
      if (variant.images && variant.images.length > 0) {
        variantData.images = variant.images.map((img) => ({
          uid: img.id || `existing-${Math.random().toString(36).substr(2, 9)}`,
          name: img.image.split("/").pop(),
          status: "done",
          url: img.image,
          id: img.id, // Ensure ID is included
        }));
      }
      
      return variantData;
    });
    
    setVariants(productVariants);
  
    // Prepare main product images
    const formattedImages = product.images?.map((img) => ({
      uid: `existing-${img.id}`,
      name: img.image.split("/").pop(),
      status: "done",
      url: img.image,
      id: img.id,
    })) || [];
  
    setImages(formattedImages);
  
    // Set form values
    form.setFieldsValue({
      title_uz: product.title_uz,
      title_ru: product.title_ru,
      title_en: product.title_en,
      description_uz: product.description_uz,
      description_ru: product.description_ru,
      description_en: product.description_en,
      category: product.category,
      is_popular: product.is_popular,
      price: product.price,
      images: formattedImages,
    });
  
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    setLoading(true);
    try {
      await api.delete(`/products/${id}/`);
      message.success("Mahsulot o'chirildi");
      await GetProducts(currentPage);
    } catch (error) {
      console.error("Delete Error:", error);
      message.error("Mahsulotni o'chirishda xatolik");
    } finally {
      setLoading(false);
    }
  };

  if (loading || contextLoading) {
    return (
      <div className="loading-container">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="products-page">
      <div className="page-header">
        <h1>Mahsulotlar</h1>
        <Button type="primary" onClick={() => setIsModalOpen(true)}>
          + Yangi mahsulot
        </Button>
      </div>

      <div className="products-grid">
        {products.length > 0 ? (
          products.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              onEdit={() => handleEdit(product)}
              onDelete={() => handleDelete(product.id)}
            />
          ))
        ) : (
          <div className="no-products">
            <p>Mahsulotlar topilmadi</p>
          </div>
        )}
      </div>

      <CustomPagination
        current={currentPage}
        total={totalProducts}
        onChange={(page) => setCurrentPage(page)}
      />

      <Modal
        title={selectedProduct ? "Mahsulotni tahrirlash" : "Yangi mahsulot"}
        open={isModalOpen}
        onCancel={resetForm}
        footer={null}
        width={800}
        destroyOnClose
      >
        <Spin spinning={loading}>
          <Form form={form} onFinish={handleAddProduct} layout="vertical">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Nomi (UZ)"
                  name="title_uz"
                  rules={[{ required: true, message: "Majburiy maydon" }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Nomi (RU)"
                  name="title_ru"
                  rules={[{ required: true, message: "Majburiy maydon" }]}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item label="Nomi (EN)" name="title_en">
              <Input />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Tavsif (UZ)"
                  name="description_uz"
                  rules={[{ required: true, message: "Majburiy maydon" }]}
                >
                  <TextArea rows={3} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Tavsif (RU)"
                  name="description_ru"
                  rules={[{ required: true, message: "Majburiy maydon" }]}
                >
                  <TextArea rows={3} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item label="Tavsif (EN)" name="description_en">
              <TextArea rows={3} />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Kategoriya"
                  name="category"
                  rules={[{ required: true, message: "Majburiy maydon" }]}
                >
                  <Select>
                    {categorys.map((cat) => (
                      <Option key={cat.id} value={cat.id}>
                        {cat.name_uz}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Ommabop"
                  name="is_popular"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              label="Variantlar bormi?"
              name="has_variants"
              valuePropName="checked"
            >
              <Switch
                checked={hasVariants}
                onChange={(checked) => {
                  setHasVariants(checked);
                  if (!checked) setVariants([]);
                }}
              />
            </Form.Item>

            {!hasVariants && (
              <>
                <Form.Item
                  label="Narxi"
                  name="price"
                  rules={[{ required: true, message: "Narx majburiy" }]}
                >
                  <InputNumber min={1} style={{ width: "100%" }} />
                </Form.Item>

                <Form.Item
                  label="Rasmlar"
                  name="images"
                  valuePropName="fileList"
                  getValueFromEvent={normFile}
                >
                  <Upload
                    multiple
                    listType="picture-card"
                    fileList={images}
                    beforeUpload={(file) => {
                      setImages((prev) => [
                        ...prev,
                        {
                          uid: file.uid,
                          name: file.name,
                          status: "done",
                          originFileObj: file,
                        },
                      ]);
                      return false;
                    }}
                    onRemove={handleImageRemove}
                    onChange={handleImageChange}
                    accept="image/*"
                  >
                    {images.length >= 8 ? null : (
                      <div>
                        <PlusOutlined />
                        <div style={{ marginTop: 8 }}>Rasm yuklash</div>
                      </div>
                    )}
                  </Upload>
                </Form.Item>
              </>
            )}

            {hasVariants && (
              <Form.Item>
                <Button
                  type="dashed"
                  onClick={() => setIsVariantModalOpen(true)}
                  block
                >
                  Variantlar qo'shish
                </Button>

                {variants.length > 0 && (
                  <div className="variants-preview" style={{ marginTop: 15 }}>
                    {variants.map((variant, index) => (
                      <div key={index}>
                        <p>
                          O'lcham: {variant.size} | Narx: {variant.price}
                        </p>
                        <p>Rasmlar: {variant.images?.length || 0} ta</p>
                        <hr />
                      </div>
                    ))}
                  </div>
                )}
              </Form.Item>
            )}

            <div className="form-actions">
              <Button onClick={resetForm}>Bekor qilish</Button>
              <Button
                type="primary"
                htmlType="submit"
                disabled={hasVariants && variants.length === 0}
              >
                {selectedProduct ? "Saqlash" : "Qo'shish"}
              </Button>
            </div>
          </Form>
        </Spin>
      </Modal>

<Modal
  title="Variantlar"
  open={isVariantModalOpen}
  onCancel={() => setIsVariantModalOpen(false)}
  footer={null}
  width={800}
  destroyOnClose
>
<VariantForm
  variants={variants}
  setVariants={setVariants}
  onClose={() => setIsVariantModalOpen(false)}
  editingIndex={editingIndex}
  setEditingIndex={setEditingIndex}
/>
</Modal>
    </div>
  );
};

export default ProductsPage;
